import { useQuery } from '@tanstack/react-query'
import { CalendarPlus } from 'lucide-react'

import { payrollApi } from '@/api/endpoints'
import { EmptyState } from '@/components/ui/Alert'
import { SkeletonRows } from '@/components/ui/Spinner'
import { formatMonthLabel } from '@/lib/billing'
import { plural } from '@/lib/format'

/**
 * Liczba lekcji zrealizowanych z uczniem, miesiąc po miesiącu.
 *
 * Realizuje ustalenie z zakresu prac: *„liczba zrealizowanych lekcji w każdym
 * miesiącu na podstawie wprowadzanych obecności przez nauczycieli”*. Liczby
 * pochodzą z tego samego zestawienia, co raport do wypłat, więc karta ucznia
 * i rozliczenia nie mogą pokazać różnych wartości.
 */
export function StudentLessonHistory({ studentId }: { studentId: string }) {
  // Rok wstecz — tyle, ile obejmuje najdłuższy okres rozliczeniowy.
  const dzis = new Date()
  const od = new Date(dzis.getFullYear(), dzis.getMonth() - 11, 1)

  const query = useQuery({
    queryKey: ['payroll', 'student', studentId],
    queryFn: () =>
      payrollApi.studentSummary(studentId, {
        date_from: od.toISOString().slice(0, 10),
        date_to: dzis.toISOString().slice(0, 10),
      }),
    enabled: studentId !== '',
  })

  if (query.isPending) return <SkeletonRows rows={3} />

  if (query.isError) {
    return (
      <EmptyState
        icon={<CalendarPlus size={24} />}
        title="Nie udało się wczytać historii"
        description="Odśwież stronę."
      />
    )
  }

  const dane = query.data
  const miesiace = [...dane.months].reverse()

  if (miesiace.length === 0) {
    return (
      <EmptyState
        icon={<CalendarPlus size={24} />}
        title="Brak rozliczonych zajęć"
        description="Historia wypełni się, gdy nauczyciel zacznie wpisywać obecności."
      />
    )
  }

  return (
    <div className="p-6">
      <dl className="mb-4 grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-xs text-on-surface-variant">Zrealizowane</dt>
          <dd className="font-serif text-2xl text-on-surface">{dane.tally.lessons_held}</dd>
        </div>
        <div>
          <dt className="text-xs text-on-surface-variant">Do odrobienia</dt>
          <dd className="font-serif text-2xl text-on-surface">{dane.tally.makeups_due}</dd>
        </div>
      </dl>

      <ul className="divide-y divide-outline-variant text-sm">
        {miesiace.map((item) => (
          <li key={`${item.year}-${item.month}`} className="flex justify-between gap-3 py-2">
            <span className="text-on-surface-variant">{formatMonthLabel(item)}</span>
            <span className="font-semibold text-on-surface">
              {item.tally.lessons_held}{' '}
              {plural(item.tally.lessons_held, 'lekcja', 'lekcje', 'lekcji')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
